import { useEffect, useState } from "react";

import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { SearchSettingsFooter } from "@/Common/Components/Settings/SearchSettingsFooter";
import { useOverlay } from "@/Common/CustomHooks/useOverlay";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { BillingHelper } from "../Helpers/BillingHelper";
import { PayableInboxFilterSettings } from "../Models/Settings/PayableInboxFilterSettings";
import { PayableInboxValidityUIModel } from "../Models/UI/PayableInboxValidityUIModel";
import { PayableInboxActions } from "../Stores/Actions/PayableInboxActions";

import styles from "./PayableInboxSettings.module.scss";

interface PayableInboxSettingsProps {
    onClose: () => void;
    filterSettings: PayableInboxFilterSettings;
    onColumnOrderingPopupOpenClick: () => void;
}

function PayableInboxSettingsContainer(props: PayableInboxSettingsProps): JSX.Element {
    const { availableValidity, availableProviders, availableBilledBy, availableSites } = billingStore.payableInboxModel;
    const [availableProvidersFromFilters, setAvailableProvidersFromFilters] = useState(availableProviders);
    const [availableBilledByFromFilters, setAvailableBilledByFromFilters] = useState(availableBilledBy);
    const [isSaveSettingChecked, setIsSaveSettingChecked] = useState(false);
    const [creationDatesValidationError, setCreationDatesValidationError] = useState(null);

    const { getText } = useTranslation();

    const [filterSettings, setFilterSettings] = useState(
        {
            ...props.filterSettings, CreationDateFrom: props.filterSettings.CreationDateFrom ?? BillingHelper.GetSearchDefaultFromDate(),
            CreationDateTo: props.filterSettings.CreationDateTo ?? BillingHelper.GetSearchDefaultToDate()
        }
    );

    useOverlay(
        "PayableInboxSettingsContainerOverlay",
        {
            transparent: true,
            onClicked: () => PayableInboxActions.saveFilterSettings(filterSettings, props.onClose, isSaveSettingChecked)
        }
    );

    const getIsFormInvalid = (): boolean => !!creationDatesValidationError;

    useEffect(() => {
        const selectedSitesIdsDictionary = filterSettings?.SiteIds.distinct().toDictionary(p => p);
        let filteredClinicianProviderBySiteAndName = availableProviders;
        let filteredClinicianBilledByBySiteAndName = availableBilledBy;

        filteredClinicianProviderBySiteAndName = filteredClinicianProviderBySiteAndName.filter(p => p.Sites.any(site => !!selectedSitesIdsDictionary?.[site]));
        filteredClinicianBilledByBySiteAndName = filteredClinicianBilledByBySiteAndName.filter(p => p.Sites.any(site => !!selectedSitesIdsDictionary?.[site]));

        if (!IsNullOrWhiteSpace(filterSettings?.CliniciansProviderSearch)) {
            filteredClinicianProviderBySiteAndName = filteredClinicianProviderBySiteAndName.filterSearch(filterSettings?.CliniciansProviderSearch, p => p.Name);
        }
        if (!IsNullOrWhiteSpace(filterSettings?.CliniciansBilledBySearch)) {
            filteredClinicianBilledByBySiteAndName = filteredClinicianBilledByBySiteAndName.filterSearch(filterSettings?.CliniciansBilledBySearch, p => p.Name);
        }

        setAvailableProvidersFromFilters(filteredClinicianProviderBySiteAndName);
        setAvailableBilledByFromFilters(filteredClinicianBilledByBySiteAndName);
    }, [availableProviders, availableBilledBy, filterSettings?.CliniciansProviderSearch, filterSettings?.CliniciansBilledBySearch, filterSettings?.SiteIds]);

    const setSelectSite = (isChecked: boolean, site: SiteUIModel): void => {
        const nextSites = isChecked
            ? [...filterSettings.SiteIds, site.Id].distinct()
            : filterSettings.SiteIds.filter(siteId => siteId != site.Id);

        setFilterSettings({ ...filterSettings, SiteIds: nextSites });
    };
    const setSelectAllSites = (isChecked: boolean): void => {
        const nextSites = isChecked
            ? availableSites.map(site => site.Id)
            : [];

        setFilterSettings({ ...filterSettings, SiteIds: nextSites });
    };

    const setSelectProviderClinician = (isChecked: boolean, clinician: EmployeeUIModel): void => {
        const nextClinicians = isChecked
            ? [...filterSettings.CliniciansProviderContactFacetIds, clinician.ContactFacetId].distinct()
            : filterSettings.CliniciansProviderContactFacetIds.filter(clinicianId => clinicianId != clinician.ContactFacetId);

        setFilterSettings({ ...filterSettings, CliniciansProviderContactFacetIds: nextClinicians });
    };

    const setSelectAllProviderClinicians = (isChecked: boolean): void => {
        const nextClinicians = isChecked
            ? [...filterSettings.CliniciansProviderContactFacetIds, ...availableProvidersFromFilters.map(clinician => clinician.ContactFacetId)].distinct()
            : filterSettings.CliniciansProviderContactFacetIds.filter(id => !availableProvidersFromFilters.any(p => p.ContactFacetId === id));

        setFilterSettings({ ...filterSettings, CliniciansProviderContactFacetIds: nextClinicians });
    };

    const setClinicianProviderSearchText = (searchText: string): void => {
        setFilterSettings({ ...filterSettings, CliniciansProviderSearch: searchText });
    };

    const setSelectBilledByClinician = (isChecked: boolean, clinician: EmployeeUIModel): void => {
        const nextClinicians = isChecked
            ? [...filterSettings.CliniciansBilledByContactFacetIds, clinician.ContactFacetId].distinct()
            : filterSettings.CliniciansBilledByContactFacetIds.filter(clinicianId => clinicianId != clinician.ContactFacetId);

        setFilterSettings({ ...filterSettings, CliniciansBilledByContactFacetIds: nextClinicians });
    };

    const setSelectAllBilledByClinicians = (isChecked: boolean): void => {
        const nextClinicians = isChecked
            ? [...filterSettings.CliniciansBilledByContactFacetIds, ...availableBilledByFromFilters.map(clinician => clinician.ContactFacetId)].distinct()
            : filterSettings.CliniciansBilledByContactFacetIds.filter(id => !availableBilledByFromFilters.any(p => p.ContactFacetId === id));

        setFilterSettings({ ...filterSettings, CliniciansBilledByContactFacetIds: nextClinicians });
    };

    const setClinicianBilledBySearchText = (searchText: string): void => {
        setFilterSettings({ ...filterSettings, CliniciansBilledBySearch: searchText });
    };

    const setSelectValidity = (isChecked: boolean, validity: PayableInboxValidityUIModel): void => {
        const nextValidity = isChecked
            ? [...filterSettings.ValidityEnums, validity.validity].distinct()
            : filterSettings.ValidityEnums.filter(validityId => validityId != validity.validity);

        setFilterSettings({ ...filterSettings, ValidityEnums: nextValidity });
    };
    const setSelectAllValidity = (isChecked: boolean): void => {
        const nextValidities = isChecked
            ? availableValidity.map(validity => validity.validity)
            : [];

        setFilterSettings({ ...filterSettings, ValidityEnums: nextValidities });
    };

    const onFilterSettingsChange = (filterSettings: PayableInboxFilterSettings): void => {
        const validationError = ValidationHelper.validateSearchDateRangeMax5Years(filterSettings.CreationDateFrom, filterSettings.CreationDateTo);
        setCreationDatesValidationError(validationError);
        if (!validationError)
            setFilterSettings(filterSettings);
    };

    return (
        <div className={styles.payableInboxSettings}>
            <div
                className={styles.header}
            >
                <span>{getText("DateLabel")}</span>
            </div>
            <div
                className={styles.header}
            >
                <CheckboxControl
                    label={getText("SitesLabel")}
                    checked={filterSettings.SiteIds?.length === availableSites.length}
                    onChange={isChecked => setSelectAllSites(isChecked)}
                />
            </div>
            <div
                className={styles.header}
            >
                <CheckboxControl
                    label={getText("ProviderLabel")}
                    checked={availableProvidersFromFilters.length > 0
                        && availableProvidersFromFilters.every(p => filterSettings.CliniciansProviderContactFacetIds?.includes(p.ContactFacetId))
                    }
                    onChange={isChecked => setSelectAllProviderClinicians(isChecked)}
                />
            </div>
            <div
                className={styles.header}
            >
                <CheckboxControl
                    label={getText("BilledByLabel")}
                    checked={availableBilledByFromFilters.length > 0
                        && availableBilledByFromFilters.every(p => filterSettings.CliniciansBilledByContactFacetIds?.includes(p.ContactFacetId))
                    }
                    onChange={isChecked => setSelectAllBilledByClinicians(isChecked)}
                />
            </div>
            <div
                className={styles.header}
            >
                <CheckboxControl
                    label={getText("ValidityLabel")}
                    checked={filterSettings.ValidityEnums?.length === availableValidity.length}
                    onChange={isChecked => setSelectAllValidity(isChecked)}
                />
            </div>

            <div className={styles.column}>
                <MultiDateSelector
                    id="creation_dates"
                    label={getText("CreationDateLabel")}
                    from={filterSettings.CreationDateFrom}
                    fromChange={date => onFilterSettingsChange({ ...filterSettings, CreationDateFrom: date })}
                    fromSetTodayLabel={getText("TodayLabel")}

                    to={filterSettings.CreationDateTo}
                    toChange={date => onFilterSettingsChange({ ...filterSettings, CreationDateTo: date })}
                    toSetTodayLabel={getText("TodayLabel")}
                    errorMessage={creationDatesValidationError}
                />
                <MultiDateSelector
                    id="visit_dates"
                    label={getText("VisitDateLabel")}
                    from={filterSettings.VisitDateFrom}
                    fromChange={date => onFilterSettingsChange({ ...filterSettings, VisitDateFrom: date })}
                    fromSetTodayLabel={getText("TodayLabel")}

                    to={filterSettings.VisitDateTo}
                    toChange={date => onFilterSettingsChange({ ...filterSettings, VisitDateTo: date })}
                    toSetTodayLabel={getText("TodayLabel")}
                />
            </div>

            <div className={styles.column}>
                {availableSites.map(site => (
                    <CheckboxControl
                        id={`site_${site.Id}`}
                        label={site.Name}
                        maxLen={25}
                        checked={filterSettings.SiteIds?.contains(site.Id)}
                        onChange={isChecked => setSelectSite(isChecked, site)}
                        key={`site_${site.Id}`}
                    />
                ))}
            </div>

            <div className={`${styles.column} ${styles.clinicianColumn}`}>
                <TextboxControl
                    id="clinicians_provider_search_text"
                    value={filterSettings.CliniciansProviderSearch}
                    onChange={(text: string) => setClinicianProviderSearchText(text)}
                    className="form-control"
                    placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                    key="clinicians_provider_search_text"
                />
                <div className={styles.clinicianList}>
                    {availableProvidersFromFilters.map(clinician => (
                        <CheckboxControl
                            id={`clinicians_provider_${clinician.Id}`}
                            label={clinician.Name}
                            checked={filterSettings.CliniciansProviderContactFacetIds?.contains(clinician.ContactFacetId)}
                            onChange={isChecked => setSelectProviderClinician(isChecked, clinician)}
                            key={`clinicians_${clinician.Id}`}
                        />

                    ))}
                </div>
            </div>

            <div className={`${styles.column} ${styles.clinicianColumn}`}>
                <TextboxControl
                    id="clinicians_billedby_search_text"
                    value={filterSettings.CliniciansBilledBySearch}
                    onChange={(text: string) => setClinicianBilledBySearchText(text)}
                    className="form-control"
                    placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                    key="clinicians_billedby_search_text"
                />
                <div className={styles.clinicianList}>
                    {availableBilledByFromFilters.map(clinician => (
                        <CheckboxControl
                            id={`clinicians_billedby_${clinician.Id}`}
                            label={clinician.Name}
                            checked={filterSettings.CliniciansBilledByContactFacetIds?.contains(clinician.ContactFacetId)}
                            onChange={isChecked => setSelectBilledByClinician(isChecked, clinician)}
                            key={`clinicians_${clinician.Id}`}
                        />
                    ))}
                </div>
            </div>

            <div className={styles.column}>
                {availableValidity.map(validity => (
                    <CheckboxControl
                        id={`proposal_${validity.validity}`}
                        label={validity.label}
                        checked={filterSettings.ValidityEnums?.contains(validity.validity)}
                        onChange={isChecked => setSelectValidity(isChecked, validity)}
                        key={`proposal_${validity.validity}`}
                    />
                ))}
            </div>

            <div className={styles.footer}>
                <SearchSettingsFooter
                    onClose={props.onClose}
                    onOk={() => PayableInboxActions.saveFilterSettings(filterSettings, props.onClose, isSaveSettingChecked)}
                    onColumnOrderingButtonClick={() => props.onColumnOrderingPopupOpenClick()}
                    onResultsPerPageChange={pageSize => setFilterSettings({ ...filterSettings, Pagination: { ...filterSettings.Pagination, PageSize: pageSize } })}
                    selectedPageSizeOption={filterSettings.Pagination.PageSize}
                    pageSizeOptions={[50, 100, 150]}
                    onSaveSettingsCheckboxChange={value => setIsSaveSettingChecked(value)}
                    disableOkButton={getIsFormInvalid()}
                />
            </div>
        </div>
    );
}

export { PayableInboxSettingsContainer };
